import { StateCreator } from "zustand";
import { IAttachment } from "@shared/mirage/core/contracts/IAttachment";
import { Template, TemplateField, TemplateResolveResult } from "@shared/mirage/services/ITemplatesService";
import { Recipient } from "@mirage/components/correspondence/Correspondence";

export type DocxPopupMode = "docx-only" | "email" | "pdf-print" | "template" | "docxAndPdf" | "call" | "draft" | "read-only";

export interface DocxPopupSaveOptions {
    base64Docx?: string | null;
    html?: string;
    pdfBase64?: string | null;
    hasDocumentChanged?: boolean;
    closeEditor?: () => void;
}

export interface DocxPopupSendEmailOptions {
    to?: string;
    cc?: string;
    bcc?: string;
    subject?: string;
    emailTemplateId?: string;
    attachments?: IAttachment[];
    allowSelectAttachments?: boolean;
    templates?: Template[];
    resolveTemplate?: (templateId: string) => Promise<TemplateResolveResult>;
    onAttachmentListRequested?: () => Promise<IAttachment[]>;
    recipients?: Recipient[];
}

export interface DocxClientUploadOptions {
    enabled: boolean;
    customButtonCaption?: string;
}

export interface DocxPopupTemplateOptions {
    to?: string;
    cc?: string;
    bcc?: string;
    subject?: string;
    templateId?: string;
    templateCaption?: string;
    fields: TemplateField[];
    type?: string;
}

export interface DocxPopupOptions {
    email?: DocxPopupSendEmailOptions;
    template?: DocxPopupTemplateOptions;
    uploadClientDocx?: DocxClientUploadOptions;
}

export interface DocxPopupShowOptions {
    header?: string;
    mode: DocxPopupMode;
    base64?: string | null;
    isHtmlBase64?: boolean;
    options?: DocxPopupOptions;
    onSave?: (saveOptions: DocxPopupSaveOptions) => Promise<void>;
    onCancel?: () => void;
    buttonsCaptions?: {
        save: string;
        cancel: string;
    };
    keepOpenAfterSave?: boolean;
    protected?: boolean;
    onAutoSave?: (base64: string) => void;
    onRestore?: () => string | null;
}

export interface DocxPopupState extends DocxPopupShowOptions {
    isVisible: boolean;
    isConfirmDialogOpen: boolean;
    documentChangedCount: number;
}

export interface DocxPopupSlice {
    docxPopup: DocxPopupState;
    setDocxPopup: (state: DocxPopupState) => void;
    updateDocxPopup: (partialState: Partial<DocxPopupState>) => void;
    resetDocxPopup: () => void;
    updateDocxPopupOptions: (partialState: Partial<DocxPopupOptions>) => void;
}

const initialDocxPopupState: DocxPopupState = {
    isVisible: false,
    base64: "",
    isConfirmDialogOpen: false,
    documentChangedCount: 0,
    mode: "docx-only",
};

export const createDocxPopupSlice: StateCreator<DocxPopupSlice, [], [], DocxPopupSlice> = (set) => ({
    docxPopup: initialDocxPopupState,

    setDocxPopup: (state) => set({ docxPopup: state }),
    updateDocxPopup: (partialState) => set((state) => ({ docxPopup: { ...state.docxPopup, ...partialState } })),
    resetDocxPopup: () => set({ docxPopup: initialDocxPopupState }),
    updateDocxPopupOptions: (partialState) => set((state) => ({ docxPopup: { ...state.docxPopup, options: { ...state.docxPopup.options, ...partialState } } })),
});
